// Service hook (TS-2.5.01, Resource Accessor layer) - the only place this feature calls
// auditApi.query. Mirrors useProjects/useDeckVersions' shape: fetch on mount and whenever the
// caller's memoized params change. No client-side scope narrowing is applied here - decisions.md
// D-005's "PM sees own actions only" is enforced server-side (AuditManager), so this hook sends
// exactly the filters the caller chose and renders exactly the page that comes back.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { AuditEntry, PagedResult } from "../../../api/generated/contracts";
import type { AuditQueryParams } from "../../../api/audit";
import { auditApi } from "../../../api/audit";

export interface UseAuditQueryResult {
  readonly page: PagedResult<AuditEntry> | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
}

export function useAuditQuery(params: AuditQueryParams): UseAuditQueryResult {
  const [page, setPage] = useState<PagedResult<AuditEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void auditApi
      .query(params)
      .then((next) => {
        setPage(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The audit log could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(refresh, [refresh]);

  return { page, loading, error, refresh };
}
